// Casas de Noxara ("Cartório de Imóveis"): anúncios das casas (todas, livres, em leilão, minha) e a escritura da casa escolhida
// com leilão, aluguel, entrar, convidados, abrir a visitas, decoração (recolher) e deixar a casa.
// CSS em client/css-parts/houses.css (prefixo .hx-).
import { useEffect, useMemo, useState } from 'preact/hooks';
import { HOUSES, HOUSE_BID_STEP, HOUSE_SIZES, type HouseDef, type HouseInfo } from '../../../../shared/src/game/houses';
import { catalogNow } from '../items';

type Send = (op: string, id?: number, arg?: string | number) => void;
type Status = 'mine' | 'owned' | 'auction' | 'free';
const n0 = (n: number) => n.toLocaleString('pt-BR');
/** 1.500.000 → 1,5kk (para os cards) */
const gp = (n: number) => (n >= 1_000_000 ? `${(n / 1_000_000).toLocaleString('pt-BR', { maximumFractionDigits: 2 })}kk` : n >= 1000 ? `${(n / 1000).toLocaleString('pt-BR', { maximumFractionDigits: 1 })}k` : String(n));
const when = (t?: number) => (t ? new Date(t).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : '—');
/** tempo que falta, curto: 3d 4h / 5h 12m / 8m */
const left = (t: number | undefined, now: number) => {
  if (!t) return '—';
  const ms = t - now;
  if (ms <= 0) return 'encerrando';
  const m = Math.floor(ms / 60_000), h = Math.floor(m / 60), d = Math.floor(h / 24);
  return d ? `${d}d ${h % 24}h` : h ? `${h}h ${m % 60}m` : `${Math.max(1, m)}m`;
};
const SIZE_ICON: Record<HouseDef['size'], string> = { small: '🏠', medium: '🏡', large: '🏘️', guild: '🏰' };
const STATUS_LABEL: Record<Status, string> = { mine: 'Sua', owned: 'Ocupada', auction: 'Em leilão', free: 'Livre' };

export function HousesWin(props: { list: HouseInfo[]; me: number; myName: string; bank: number; guildLeader: boolean; inHouse: number | null; send: Send }) {
  const [filter, setFilter] = useState<'all' | 'free' | 'auction' | 'mine'>('all');
  const info = useMemo(() => new Map(props.list.map((h) => [h.id, h])), [props.list]);
  const mine = props.list.find((h) => h.ownerId === props.me || h.bidderId === props.me);
  const [sel, setSel] = useState<number>(mine?.id ?? HOUSES[0]!.id);
  const [bid, setBid] = useState(0);
  const [guest, setGuest] = useState('');
  const [help, setHelp] = useState(false);
  const [, tick] = useState(0);
  useEffect(() => { const t = setInterval(() => tick((x) => x + 1), 30_000); return () => clearInterval(t); }, []);
  const now = Date.now();

  const statusOf = (id: number): Status => {
    const s = info.get(id);
    if (s?.ownerId === props.me || s?.bidderId === props.me) return 'mine';
    if (s?.ownerId) return 'owned';
    if (s?.bidderId) return 'auction';
    return 'free';
  };
  const counts = { free: 0, auction: 0, owned: 0 };
  for (const h of HOUSES) { const s = info.get(h.id); if (s?.ownerId) counts.owned++; else if (s?.bidderId) counts.auction++; else counts.free++; }
  const shown = HOUSES.filter((h) => {
    const i = info.get(h.id);
    if (filter === 'free') return !i?.ownerId && !i?.bidderId;
    if (filter === 'auction') return !!i?.bidderId;
    if (filter === 'mine') return i?.ownerId === props.me || i?.bidderId === props.me;
    return true;
  });
  const pick = (id: number) => { setSel(id); setBid(0); };

  const def = HOUSES.find((h) => h.id === sel)!;
  const i = info.get(sel) ?? { id: sel };
  const st = statusOf(sel);
  const owner = i.ownerId === props.me;
  const minBid = i.bidderId ? Math.ceil((i.bid ?? 0) * HOUSE_BID_STEP) : def.minBid;
  const z = HOUSE_SIZES[def.size];
  const bidVal = bid || minBid;
  const overdue = !!i.ownerId && !!i.paidUntil && i.paidUntil < now;
  const bidBlock = bidVal < minBid ? `O lance mínimo é ${n0(minBid)} gp.` : props.bank < bidVal ? 'Gold insuficiente no banco.' : i.bidderId === props.me ? 'Você já tem o maior lance.' : '';
  const mineDef = mine ? HOUSES.find((h) => h.id === mine.id) : undefined;

  return (
    <div class="hx">
      {/* cabeçalho: o cartório de imóveis */}
      <div class="hx-head">
        <div class="hx-head-seal"><img src="/ui/btn-casas-up.png" alt="" /></div>
        <div class="hx-head-txt">
          <div class="hx-title">Cartório de Imóveis de Noxara <button class="hx-help" onClick={() => setHelp(!help)} title="Como funciona">?</button></div>
          <div class="hx-sub">Escrituras, leilões e aluguéis · uma casa por conta</div>
          <div class="hx-stats">
            <span><i class="hx-dot free" /> {counts.free} livres</span>
            <span><i class="hx-dot auction" /> {counts.auction} em leilão</span>
            <span><i class="hx-dot owned" /> {counts.owned} ocupadas</span>
          </div>
        </div>
        <div class="hx-bank" title="Lances e aluguel saem do banco"><small>Seu banco</small><b>{n0(props.bank)} gp</b></div>
      </div>

      {(help || !mine) && (
        <div class="hx-steps">
          <div><i>🔨</i><b>1. Dê um lance</b><small>O leilão dura 7 dias a partir do primeiro lance. Cada lance novo precisa ser {Math.round((HOUSE_BID_STEP - 1) * 100)}% maior.</small></div>
          <div><i>📜</i><b>2. Pague o aluguel</b><small>Semanal, descontado do banco. O primeiro só vence 7 dias depois de ganhar.</small></div>
          <div><i>🪑</i><b>3. Decore</b><small>Dentro da casa, botão direito num item de decoração da mochila → Colocar na casa.</small></div>
          <div><i>🗝️</i><b>4. Convide</b><small>Chame amigos pelo nome ou abra a casa a visitas. Boneco de treino: +10% no treino.</small></div>
        </div>
      )}

      {/* destaque: a casa (ou o lance) do jogador */}
      {mine && mineDef && (
        <div class={'hx-mine' + (mine.ownerId === props.me ? '' : ' bidding')}>
          <span class="hx-mine-ico">{SIZE_ICON[mineDef.size]}</span>
          <div class="hx-mine-txt">
            <small>{mine.ownerId === props.me ? 'Sua casa' : 'Seu lance'}</small>
            <b>{mineDef.name}</b>
            <span>{mine.ownerId === props.me
              ? (mine.paidUntil && mine.paidUntil < now ? <em class="hx-warn">Aluguel atrasado</em> : <>Aluguel pago até {when(mine.paidUntil)}</>)
              : <>Lance de {n0(mine.bid ?? 0)} gp · termina em {left(mine.auctionEnd, now)}</>}</span>
          </div>
          {mine.id !== sel && <button class="tb-button" onClick={() => pick(mine.id)}>Ver escritura</button>}
          {mine.ownerId === props.me && <button class="tb-button green" onClick={() => props.send('enter', mine.id)}>Entrar</button>}
        </div>
      )}

      <div class="hx-body">
        {/* anúncios */}
        <div class="hx-side">
          <div class="hx-filters">
            {([['all', 'Todas', HOUSES.length], ['free', 'Livres', counts.free], ['auction', 'Leilão', counts.auction], ['mine', 'Minha', mine ? 1 : 0]] as const).map(([id, l, c]) =>
              <button key={id} class={'tb-button' + (filter === id ? ' green' : '')} onClick={() => setFilter(id)}>{l} <small>{c}</small></button>)}
          </div>
          <div class="hx-list">
            {shown.map((h) => {
              const s = info.get(h.id), k = statusOf(h.id);
              return (
                <div key={h.id} class={`hx-card ${k}${h.id === sel ? ' on' : ''}${h.size === 'guild' ? ' guild' : ''}`} onClick={() => pick(h.id)}>
                  <span class="hx-card-ico">{SIZE_ICON[h.size]}</span>
                  <div class="hx-card-main">
                    <b class="hx-card-name">{h.name}</b>
                    <small>{HOUSE_SIZES[h.size].label} · aluguel {gp(h.rent)}/sem</small>
                    {k === 'auction' || (k === 'mine' && s?.bidderId) ? (
                      <span class="hx-card-bid">🔨 <b>{gp(s?.bid ?? 0)}</b> · ⏳ {left(s?.auctionEnd, now)}</span>
                    ) : s?.ownerId ? <span class="hx-card-owner">{s.owner}{s.guild ? ` · ${s.guild}` : ''}</span>
                      : <span class="hx-card-min">a partir de <b>{gp(h.minBid)}</b></span>}
                  </div>
                  <span class={'hx-badge ' + k}>{STATUS_LABEL[k]}</span>
                </div>
              );
            })}
            {!shown.length && <div class="hx-empty">{filter === 'mine' ? 'Você ainda não tem casa nem lance.' : 'Nenhuma casa aqui.'}</div>}
          </div>
        </div>

        {/* escritura da casa escolhida */}
        <div class={'hx-deed ' + st}>
          <div class={'hx-seal ' + st} title={STATUS_LABEL[st]}>{st === 'mine' ? '★' : st === 'owned' ? '✓' : st === 'auction' ? '⚖' : '◆'}</div>
          <div class="hx-deed-kind">Escritura nº {String(def.id).padStart(3, '0')} · {z.label}</div>
          <h4 class="hx-deed-name">{SIZE_ICON[def.size]} {def.name}</h4>
          <div class="hx-deed-grid">
            <div><small>Lance inicial</small><b>{n0(def.minBid)} gp</b></div>
            <div><small>Aluguel/semana</small><b>{n0(def.rent)} gp</b></div>
            <div><small>Decoração</small><b>{i.ownerId && i.decor != null ? `${i.decor}/` : 'até '}{z.decor} itens</b></div>
          </div>

          {i.ownerId ? (
            <div class="hx-deed-sec">
              <div class="hx-kv"><span>Proprietário</span><b>{i.owner}{i.guild ? ` (guild ${i.guild})` : ''}</b></div>
              <div class="hx-kv"><span>Aluguel pago até</span><b class={overdue ? 'hx-warn' : ''}>{when(i.paidUntil)}{overdue ? ' · atrasado' : ''}</b></div>
              <div class="hx-actions">
                <button class="tb-button green" onClick={() => props.send('enter', sel)}>Entrar</button>
                {i.open ? <span class="hx-tag">🚪 Aberta a visitas</span> : null}
                {props.inHouse === sel ? <span class="hx-tag">📍 Você está aqui</span> : null}
              </div>
            </div>
          ) : (
            <div class="hx-deed-sec">
              {i.bidderId ? (
                <div class="hx-auction">
                  <div><small>Maior lance</small><b>{n0(i.bid ?? 0)} gp</b><span>de {i.bidder}{i.bidderId === props.me ? ' (você)' : ''}</span></div>
                  <div><small>Termina em</small><b>{left(i.auctionEnd, now)}</b><span>{when(i.auctionEnd)}</span></div>
                </div>
              ) : <p class="hx-note">Sem lances ainda. O leilão de 7 dias começa no primeiro lance.</p>}
              {def.size === 'guild' && !props.guildLeader ? <p class="hx-note warn">Só líderes de guild dão lance em guildhall.</p> : (
                <>
                  <div class="hx-bidrow">
                    <input type="number" min={minBid} value={bidVal} onInput={(e) => setBid(Math.floor(Number((e.target as HTMLInputElement).value) || 0))} />
                    <button class="tb-button green" disabled={!!bidBlock} title={bidBlock || undefined}
                      onClick={() => props.send('bid', sel, bidVal)}>🔨 Dar lance</button>
                  </div>
                  <small class={'hx-hint' + (bidBlock ? ' warn' : '')}>{bidBlock || `Mínimo agora: ${n0(minBid)} gp`}</small>
                </>
              )}
              <p class="hx-note">O gold do lance sai do banco e volta se alguém der um lance maior. Quem ganha paga o primeiro aluguel só daqui a 7 dias.
                {mine && mine.id !== sel ? ' Sua conta já tem uma casa ou um lance em outra.' : ''}</p>
            </div>
          )}

          {owner && (
            <div class="hx-owner">
              <div class="hx-owner-title">🗝️ Sua casa</div>
              <label class="hx-check"><input type="checkbox" checked={!!i.open} onChange={() => props.send('open', sel)} /> Aberta a visitas (qualquer jogador entra)</label>
              <div class="hx-bidrow">
                <input placeholder="Nome do convidado" value={guest} onInput={(e) => setGuest((e.target as HTMLInputElement).value)} />
                <button class="tb-button" disabled={!guest.trim()} onClick={() => { props.send('guestAdd', sel, guest.trim()); setGuest(''); }}>Convidar</button>
              </div>
              <div class="hx-guests">
                {(i.guests ?? []).map((g) => <span key={g} class="hx-chip">{g} <a onClick={() => props.send('guestDel', sel, g)} title="Remover">×</a></span>)}
                {!(i.guests ?? []).length && <span class="hx-note">Nenhum convidado.</span>}
              </div>
              <p class="hx-note">🪑 Decoração: entre na casa, vire para um espaço livre e clique com o botão direito num item de decoração da mochila → <b>Colocar na casa</b>.
                {props.inHouse === sel ? '' : ' (Você precisa estar dentro da casa.)'} O boneco de treino da casa dá +10% no treino com exercise weapon.</p>
              <DecorList id={sel} send={props.send} />
              <button class="tb-button hx-leave" onClick={() => { if (confirm('Deixar a casa? A decoração vai para o seu depot e você não recebe o gold de volta.')) props.send('leave', sel); }}>Deixar a casa</button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** itens colocados (vem do mapa da casa quando o jogador está dentro) */
let decorNow: number[][] = [];
export function setDecorList(list: number[][]) { decorNow = list; }
function DecorList(props: { id: number; send: Send }) {
  const cat = catalogNow();
  if (!decorNow.length) return null;
  return (
    <ul class="hx-decor">
      {decorNow.map((d, k) => <li key={k}><span>{cat?.get(d[3]!)?.name ?? d[3]}</span><button class="tb-button" onClick={() => props.send('take', props.id, k)}>Recolher</button></li>)}
    </ul>
  );
}
